<template>
    <div class="relative isolate bg-white py-24 sm:py-32">
        <div class="absolute inset-x-0 top-0 -z-10 flex justify-center overflow-hidden blur-3xl" aria-hidden="true">
            <div class="aspect-[1155/678] w-[60rem] bg-gradient-to-tr from-primary-100 to-primary-50 opacity-60"
                style="clip-path: polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)" />
        </div>

        <Container>
            <SectionHeading eyebrow="Pricing" title="Choose the right plan for you"
                description="Unlock your potential in Arabic with flexible plans for learners and educators. Whether you're just starting or looking to advance, our pricing ensures you get the resources and support you need to succeed." />

            <div>
                <Tabs :value=value>
                    <div class="flex justify-center items-center mt-12">
                        <TabList>
                            <Tab value="0" as="div" class="flex items-center gap-2">
                                <span class="font-bold whitespace-nowrap">30 Minutes</span>
                            </Tab>
                            <Tab value="1" as="div" class="flex items-center gap-2">
                                <span class="font-bold whitespace-nowrap">60 Minutes</span>
                            </Tab>
                        </TabList>
                    </div>
                    <TabPanels>
                        <TabPanel v-for="(group, groupIdx) in tiers" :key="groupIdx" :value="String(groupIdx)" as="p" class="m-0">
                            <div class="mx-auto mt-12 grid max-w-lg grid-cols-1 gap-6 lg:max-w-none lg:grid-cols-4">
                                <div v-for="tier in group?.tiers" :key="tier.id" :class="[
                                        tier.featured ? 'bg-gray-950 ring-accent-400 lg:-my-2 lg:scale-105 shadow-xl' : 'bg-white ring-gray-200 shadow-sm',
                                        'relative flex flex-col rounded-3xl p-8 ring-1']">
                                    <span v-if="tier.featured"
                                        class="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-white shadow-sm">
                                        Most Popular
                                    </span>
                                    <h3 :id="tier.id"
                                        :class="[tier.featured ? 'text-accent-400' : 'text-primary-600', 'text-sm font-semibold uppercase tracking-wide']">
                                        {{ tier.name }}
                                    </h3>
                                    <p class="mt-4 flex items-baseline gap-x-2">
                                        <span :class="[tier.featured ? 'text-white' : 'text-gray-900', 'text-5xl font-semibold tracking-tight']">
                                            ${{ Number(tier.price) }}</span>
                                        <span :class="[tier.featured ? 'text-gray-400' : 'text-gray-500', 'text-base']">/month</span>
                                    </p>
                                    <p :class="[tier.featured ? 'text-gray-300' : 'text-gray-600', 'mt-4 text-sm/6']">
                                        {{ tier.description }}
                                    </p>
                                    <ul role="list"
                                        :class="[tier.featured ? 'text-gray-300' : 'text-gray-600', 'mt-6 flex-1 space-y-3 text-sm/6']">
                                        <li v-for="feature in tier.features" :key="feature" class="flex gap-x-3">
                                            <CheckIcon
                                                :class="[tier.featured ? 'text-accent-400' : 'text-primary-600', 'h-5 w-5 flex-none']"
                                                aria-hidden="true" />
                                            {{ feature }}
                                        </li>
                                    </ul>
                                    <router-link :to="{ name: 'courses' }" :aria-describedby="tier.id" class="mt-8">
                                        <Button :variant="tier.featured ? 'accent' : 'outline'" size="md" block>
                                            Enroll now
                                        </Button>
                                    </router-link>
                                </div>
                            </div>
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </div>
        </Container>
    </div>
</template>

<script setup>
import { CheckIcon } from '@heroicons/vue/20/solid';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import { ref } from 'vue';
import Container from '@/components/ui/Container.vue';
import SectionHeading from '@/components/ui/SectionHeading.vue';
import Button from '@/components/ui/Button.vue';
import { getTiers } from '@/services/tierService';

const value = ref('0');
const tiers = ref({});

getTiers().then((data) => {
    tiers.value = data.data;
});

</script>

<style>
.p-tabpanels {
    background-color: transparent !important;
}
</style>
